import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { map } from 'rxjs/operators';
import { BuildConfigsService } from './build-configs.service';
import { BuildConfig } from '../model/build-config';
import { BuildsService } from './builds.service';
import { Build } from '../model/build';
import { BuildLog } from '../model/build-log';
import CustomStore from 'devextreme/data/custom_store';
import { Observable } from 'rxjs';
import { BuildArtifact } from '../model/build-artifact';
import { saveAs } from 'file-saver';
import { HttpEventType } from '@angular/common/http';
import { FormUtil } from '../shared/util/form-util';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { JiraService } from './jira.service';
import { JiraProject } from '../model/jira-project';

@Component({
  selector: 'app-build-config',
  templateUrl: './build-config.component.html',
  styleUrls: ['./build-config.component.css']
})
export class BuildConfigComponent implements OnInit {

  _config: BuildConfig;
  builds: Build[] = [];
  logsDataSourceStorage: any = [];
  artifactsDataSourceStorage: any = [];
  downloadStatus: any = [];
  expandedRowKey: any;

  editBuildConfig: boolean = false;
  showAddBuild: boolean = false;
  buildReleaseNotes: Build;

  readonly isValid = FormUtil.isValid;

  formReleaseNotes: FormGroup;
  formBuildConfig: FormGroup;
  formAddBuild: FormGroup;

  jiraProjects: JiraProject[] = [];

  constructor(
    private buildConfigService: BuildConfigsService,
    private jiraService: JiraService,
    private fb: FormBuilder,
    private buildService: BuildsService,
    private route: ActivatedRoute) {

    this.changeReleaseNoteMethod = this.changeReleaseNoteMethod.bind(this);

    this.formBuildConfig = this.fb.group({
      name: ['', Validators.required],
      application: ['', Validators.required],
      externalId: ['', Validators.required],
      gitRepository: ['', Validators.required],
      jiraId: ['', Validators.required],
      jiraCustomerLabel: ['', Validators.required],
      releaseNotesEmail: ['', FormUtil.commaSepEmail]
    });

    this.formReleaseNotes = this.fb.group({
      method: ['Download Only', Validators.required]
    });

    this.formAddBuild = this.fb.group({
      release: ['', Validators.required]
    });
  }

  ngOnInit() {

    this.jiraService.getProjects().subscribe(jiraProjects => this.jiraProjects = jiraProjects);

    this.route.paramMap.pipe(
      map(params => params.get('id')))
      .subscribe((id: string) => {
        if (id) {
          this.buildConfigService.getConfig(+id).subscribe(config => this.config = config);
        }
      });
  }

  set config(config: BuildConfig) {
    this._config = config;
    this.buildService.getBuilds(config).subscribe(builds => {
      this.builds = builds;
      setTimeout(() => this.refresh(), 30 * 1000);
    });
  }


  get config(): BuildConfig {
    return this._config;
  }

  onToolbarPreparing(e) {
    e.toolbarOptions.items.unshift(
      {
        location: 'before',
        widget: 'dxButton',
        options: {
          icon: 'refresh',
          onClick: () => {
            this.refresh()
          }
        }
      },
      {
        location: 'before',
        widget: 'dxButton',
        options: {
          icon: 'fal fa-plus',
          onClick: () => {
            this.addBuild()
          }
        }
      },
      {
        location: 'after',
        template: 'gridTitle'
      });
  }

  refresh() {
    this.buildService.getBuilds(this.config)
      .subscribe(builds => {
        this.builds = builds;
        this.logsDataSourceStorage = [];
        this.artifactsDataSourceStorage = [];

        setTimeout(() => this.refresh(), 30 * 1000);
      });
  }

  getLogs(build: Build) {
    let item = this.logsDataSourceStorage.find((i) => i.key === build.id);
    if (!item) {
      item = {
        key: build.id,
        dataSourceInstance: this.buildLogsDataSource(build)
      };
      this.logsDataSourceStorage.push(item)
    }
    return item.dataSourceInstance;
  }

  getArtifacts(build: Build) {
    let item = this.artifactsDataSourceStorage.find((i) => i.key === build.id);
    if (!item) {
      item = {
        key: build.id,
        dataSourceInstance: this.buildArtifactsDataSource(build)
      };
      this.artifactsDataSourceStorage.push(item)
    }
    return item.dataSourceInstance;
  }

  publish(build: Build) {
    this.buildService.publish(build).subscribe(() => build.published = true);

  }

  unpublish(build: Build) {
    this.buildService.unpublish(build).subscribe(() => build.published = false);
  }


  findBuild(artifact: BuildArtifact) {
    return this.builds.find(i => i.id === artifact.buildId);
  }

  downloadArtifact(build: Build, artifact: BuildArtifact) {

    let self = this;
    let item = this.downloadStatus.find((i) => i.key === artifact.name);
    if (item) {
      return; // already downloaded / downloading
    }

    item = {
      key: artifact.name,
      percentDone: 0
    };
    this.downloadStatus.push(item);


    this.buildService.downloadArtifact(build, artifact).subscribe(
      event => {
        if (event.type === HttpEventType.DownloadProgress) {
          item.percentDone = Math.round(100 * event.loaded / event.total);
        }
        if (event.type === HttpEventType.Response) {
          saveAs(event.body as Blob, artifact.name);
        }
      },
      ex => {
        for (let i = 0; i < self.downloadStatus.length; i++) {
          if (self.downloadStatus[i].key === artifact.name) {
            self.downloadStatus.splice(i, 1);
            break
          }
        }
      },
      () => {
        for (let i = 0; i < self.downloadStatus.length; i++) {
          if (self.downloadStatus[i].key === artifact.name) {
            self.downloadStatus.splice(i, 1);
            break
          }
        }
      }
    );
  }


  getDownloadStatus(artifact: BuildArtifact): string {
    let item = this.downloadStatus.find((i) => i.key === artifact.name);
    if (item) {
      return item.percentDone + ' %';
    }
    else {
      return '';
    }
  }


  onRowExpanding(e) {

    if (this.expandedRowKey !== e.key && this.expandedRowKey) {
      e.component.collapseRow(this.expandedRowKey);
    }
    this.expandedRowKey = e.key;
  }

  private buildLogsDataSource(build: Build): any {

    let self = this;
    let dataSource: any = {};

    dataSource.store = new CustomStore({
      load: function (options): any {
        let observable: Observable<BuildLog[]>;

        observable = self.buildService.getLogs(build);

        return observable.toPromise()
          .then(result => {
            return {
              data: result
            };
          });
      }
    });

    return dataSource;
  }


  private buildArtifactsDataSource(build: Build): any {

    let self = this;
    let dataSource: any = {};

    dataSource.store = new CustomStore({
      load: function (options): any {
        let observable: Observable<BuildArtifact[]>;

        observable = self.buildService.getArtifacts(build);

        return observable.toPromise()
          .then(result => {
            return {
              data: result
            };
          });
      }
    });

    return dataSource;
  }


  getReleaseNoteStatus(build: Build): string {
    let statusKey = 'release-notes.' + build.release + build.id;
    let item = this.downloadStatus.find((i) => i.key === statusKey);
    if (item) {
      return item.percentDone + ' %';
    }
    else {
      return '';
    }
  }


  changeReleaseNoteMethod() {
    if (this.formReleaseNotes.value.method === 'Email') {
      this.formReleaseNotes.addControl('email',
        this.fb.control(this.config.releaseNotesEmail, [Validators.required, Validators.email]));

      this.formReleaseNotes.addControl('subject',
        this.fb.control(this.config.application + ' Release Notes ' + this.config.name + ' ' + this.buildReleaseNotes.release,
          [Validators.required]));
    }
    else {
      this.formReleaseNotes.removeControl('email');
      this.formReleaseNotes.removeControl('subject');
    }
  }


  generateReleaseNotes(build: Build, method: string) {

    let self = this;
    let statusKey = 'release-notes.' + build.release + build.id;
    let item = this.downloadStatus.find((i) => i.key === statusKey);
    if (item) {
      return; // already downloaded / downloading
    }

    item = {
      key: statusKey,
      percentDone: 0
    };
    this.downloadStatus.push(item);

    let observable;
    if (method === 'Email') {
      observable = this.buildService.emailReleaseNotes(build, this.formReleaseNotes.value);
    }
    else {
      observable = this.buildService.getReleaseNotes(build);
    }

    observable.subscribe(
      event => {
        if (event.type === HttpEventType.DownloadProgress) {
          item.percentDone = Math.round(100 * event.loaded / event.total);
        }
        if (event.type === HttpEventType.Response) {
          this.buildReleaseNotes = null;
          saveAs(event.body as Blob, build.config.name + '-release-notes-' + build.release + '.pdf');
        }
      },
      ex => {
        for (let i = 0; i < self.downloadStatus.length; i++) {
          if (self.downloadStatus[i].key === statusKey) {
            self.downloadStatus.splice(i, 1);
            break
          }
        }
      },
      () => {
        for (let i = 0; i < self.downloadStatus.length; i++) {
          if (self.downloadStatus[i].key === statusKey) {
            self.downloadStatus.splice(i, 1);
            break
          }
        }
      }
    );
  }

  addBuild() {
    this.showAddBuild = true;
  }

  saveBuild() {
    let formValue = this.formAddBuild.value;


    // FIXME: error handling :-)
    this.buildService.addBuild(this.config, formValue.release).subscribe(
      () => {
        this.refresh();
        this.showAddBuild = false;
      }
    );
  }

  showBuildConfig() {
    let formValue = {
      name: this.config.name || null,
      application: this.config.application || null,
      externalId: this.config.externalId || null,
      gitRepository: this.config.gitRepository || null,
      jiraId: this.config.jiraId || null,
      jiraCustomerLabel: this.config.jiraCustomerLabel ? this.config.jiraCustomerLabel : null,
      releaseNotesEmail: this.config.releaseNotesEmail ? this.config.releaseNotesEmail : null
    }

    this.formBuildConfig.setValue(formValue);
    this.editBuildConfig = true;
  }

  saveBuildConfig() {
    let config = {} as BuildConfig;
    Object.assign(config, this.config);
    Object.assign(config, this.formBuildConfig.value);

    this.buildConfigService.save(config).subscribe(config => {
      this.config = config;
      this.editBuildConfig = false;
    });
  }

  getJiraDisplayText(data: JiraProject): string {
    if (!data) {
      return '';
    }

    return `${data.name} (${data.url})`;
  }
}
